/* eslint-disable react/sort-prop-types */
import React, { Fragment } from 'react';

import { IAmpPropsTag } from '../../../typings/amp';
import {
  Color,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  COLOR_SCHEMA_LIGHT,
  ColorSchema,
  FontSize,
  FontWeight,
  IDesignTokens,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { MatchStatusLiveType } from '../../../typings/match';
import { GoalType } from '../../../typings/match-formations';
import { ITeamTypings } from '../../../typings/team';
import { placeholdersMap, TEAMLOGO } from '../../../utils/placeholders-football';
import { getMappedColor, makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import AmpImg from '../../atoms/amp/amp-img/amp-img';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import Text from '../../atoms/text/text';

import { getScoreboard } from './team-utils';

import './team.scss';

export type IconPosition = 'left' | 'right' | 'vertical' | 'verticalReverse';
export type TeamLogoSize = '20' | '23' | '30' | '40' | '23--28' | '28--40' | '30--60' | '45--60' | '50';

export const iconPositions: IconPosition[] = ['left', 'right', 'vertical', 'verticalReverse'];
export const teamLogoSizes: TeamLogoSize[] = ['20', '23', '30', '40', '23--28', '28--40', '30--60', '45--60', '50'];

export interface IScoreboard {
  player: string;
  minute: number | string;
  type?: GoalType | null;
  matchStatus?: MatchStatusLiveType | null;
}

export interface ITeam extends ITeamTypings, IDesignTokens, IComponent {
  ampProps?: IAmpPropsTag[];
  clickable?: boolean;
  colorSchema?: ColorSchema;
  eliminated?: boolean;
  ellipsis?: boolean;
  homepageUrl?: string | null; // BE sends NULL value, too;
  iconPosition?: IconPosition;
  isLose?: boolean;
  logoSize?: TeamLogoSize;
  placeholder?: string;
  negativeLogoUrl?: string | null;
  scoreboard?: IScoreboard[];
}

type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;
type SpanProps = React.HTMLAttributes<HTMLSpanElement>;

export const DEFAULT_TEAM_TEXT_COLOR: Color = 'blue';
export const DEFAULT_TEAM_SCHEMA_COLOR: ColorSchema = 'light';
export const TEXT_COLOR_WITH_SCHEMA_DARK: ColorSchema = 'light';
export const TEXT_COLOR_WITH_SCHEMA_LIGHT: ColorSchema = 'dark';

export const DEFAULT_TEAM_TEXT_FONT_SIZE: FontSize = '14';
export const DEFAULT_TEAM_TEXT_FONT_WEIGHT: FontWeight = '500';
export const DEFAULT_TEAM_LOGO_SIZE: TeamLogoSize = '23';
export const DEFAULT_TEAM_ICON_POSITION: IconPosition = 'left';

const getColorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: TEXT_COLOR_WITH_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: TEXT_COLOR_WITH_SCHEMA_LIGHT,
    [COLOR_SCHEMA_DARK_LIGHT]: TEXT_COLOR_WITH_SCHEMA_DARK,
  };
};

// Unfortunately no "istanbul ignore start/end", see https://github.com/gotwarlost/istanbul/issues/445
const classNames = ({
  /* istanbul ignore next: default value */ logoSize = DEFAULT_TEAM_LOGO_SIZE,
  colorSchema,
  eliminated,
  ellipsis,
  iconPosition,
  isLose,
}: {
  logoSize: string;
  colorSchema: string;
  eliminated: boolean;
  ellipsis: boolean;
  iconPosition: string;
  isLose: boolean;
}) =>
  makeClassNames({
    team: [
      'team',
      `team--icon-${iconPosition}`,
      `team--icon-${colorSchema}`,
      {
        'team--eliminated': eliminated,
        'team--lose': isLose,
      },
    ],
    name: ['team__name', `team__name--${iconPosition}`, { 'team__name--ellipsis': ellipsis }],
    iconWrapper: ['team__icon-wrapper', `team__icon-wrapper--size-${logoSize}`],
    icon: ['team__icon', `team__icon--size-${logoSize}`],
    matchInfoWrapper: ['team__matchInfor-wrapper'],
    matchInfo: ['team__matchInfor'],
  });

const Team = ({
  /* istanbul ignore next: default value */ ampProps = [],
  /* istanbul ignore next: default value */ clickable = false,
  /* istanbul ignore next: default value */ colorSchema = DEFAULT_TEAM_SCHEMA_COLOR,
  /* istanbul ignore next: default value */ dataAttributes = {},
  /* istanbul ignore next: default value */ eliminated = false,
  /* istanbul ignore next: default value */ ellipsis = false,
  /* istanbul ignore next: default value */ fontSize = DEFAULT_TEAM_TEXT_FONT_SIZE,
  /* istanbul ignore next: default value */ fontWeight = DEFAULT_TEAM_TEXT_FONT_WEIGHT,
  /* istanbul ignore next: default value */ iconPosition = DEFAULT_TEAM_ICON_POSITION,
  /* istanbul ignore next: default value */ isLose = false,
  /* istanbul ignore next: default value */ logoSize = DEFAULT_TEAM_LOGO_SIZE,
  /* istanbul ignore next: default value */ logoUrl = '',
  /* istanbul ignore next: default value */ placeholder = placeholdersMap[TEAMLOGO],
  /* istanbul ignore next: default value */ scoreboard = [],
  color,
  homepageUrl,
  name,
}: ITeam) => {
  const cn = classNames({ colorSchema, eliminated, ellipsis, iconPosition, isLose, logoSize });
  const colorSchemaMapping = getColorSchemaMapping();
  const colorCalculated =
    color || getMappedColor({ colorSchemaMapping, colorSchema, defaultColor: DEFAULT_TEAM_TEXT_COLOR });

  // This COMPONENT contains HTML TAGS that should be CONVERTED to AMP ones?
  const ampImg: IAmpPropsTag | undefined = ampProps.find((item: IAmpPropsTag) => item.tag === 'img');

  const logoImg = logoUrl.length ? (
    <span className={cn.iconWrapper}>
      {ampImg ? (
        <AmpImg src={logoUrl} className={cn.icon} layout={ampImg.layout} width={ampImg.width} height={ampImg.height} />
      ) : (
        <ImgPlaceholder aria-hidden src={logoUrl} className={cn.icon} loading="lazy" placeholder={placeholder} />
      )}
    </span>
  ) : null;

  const groupedScoreboard = getScoreboard(scoreboard, 'player', 'minute');

  const matchInfo = scoreboard.length ? (
    <div className={cn.matchInfoWrapper}>
      {groupedScoreboard.map((goals: string) => (
        <Fragment key={goals}>
          <Text className={cn.matchInfo} color={colorCalculated} fontSize="12" fontWeight="400">
            {goals}
          </Text>
        </Fragment>
      ))}
    </div>
  ) : null;

  let TeamTag: keyof JSX.IntrinsicElements = 'span';
  let linkProps: LinkProps | SpanProps = {};

  if (clickable && homepageUrl) {
    TeamTag = 'a';
    linkProps = { href: homepageUrl };
  }

  return (
    <TeamTag className={cn.team} {...linkProps} {...objectToDataAttributes(dataAttributes)}>
      {logoImg}{' '}
      <Text className={cn.name} color={colorCalculated} fontSize={fontSize} fontWeight={fontWeight}>
        {name}
      </Text>
      {matchInfo}
    </TeamTag>
  );
};

export default Team;
